/**
 * Utility functions for route checking
 */

import {
  referralLoginSearchFromSearch,
} from './referralRouting'
import {
  isSettingsSection, SETTINGS_SECTION_PARAM, settingsSectionFromSearch,
} from './settingsSections'

/** Route of the sign-in page. */
export const LOGIN_ROUTE = '/login'

/** Where the backend lands a finished sign-in. */
export const CALLBACK_ROUTE = '/callback'

/** Where the backend lands a signup that joined the registration waitlist. */
export const PENDING_ROUTE = '/pending'

/** Isolated, analytics-free host used by the backend screenshot renderer. */
export const VISUALIZATION_RENDERER_ROUTE = '/visualization-renderer'

/** Isolated, analytics-free host the backend screenshots an assistant view in. */
export const VIEW_RENDERER_ROUTE = '/view-renderer'

/** Routes that receive agent-owned data in a backend browser and must stay analytics-free. */
export function isIsolatedRendererPath(pathname: string): boolean {
  return pathname === VISUALIZATION_RENDERER_ROUTE || pathname === VIEW_RENDERER_ROUTE
}

/**
 * The onboarding wizard replayed on canned data, for screen captures: no
 * account, no backend writes, no analytics (see OnboardingDemoPage).
 */
export const ONBOARDING_DEMO_ROUTE = '/onboarding/demo'

/**
 * Case-insensitive, as React Router's matching is: a capitalisation the app
 * shell missed would reach the in-app demo route, whose job is to reload into
 * the demo, and reload forever.
 */
export function isOnboardingDemoPath(pathname: string): boolean {
  return pathname.toLowerCase().replace(/\/+$/, '') === ONBOARDING_DEMO_ROUTE
}

type LoginSearchResolver = (search: string) => string

/** Route-owned login intent shared by declarative and imperative auth guards. */
const LOGIN_SEARCH_BY_ROUTE: Readonly<Record<string, LoginSearchResolver>> = {
  '/refer': referralLoginSearchFromSearch,
  '/integrations': integrationsLoginSearchFromSearch,
}

/**
 * Where a logged-out visit to the integrations page resumes after signing in.
 *
 * Two different errands land here. A bare visit is the connect-a-second-inbox
 * trap: the bounce to /login dropped every trace of why the user came, so they
 * signed in with the inbox they meant to ADD and got a separate account
 * (GET-9017).
 *
 * A visit naming an integration to connect is the other errand, and
 * `connect-inbox` is the wrong answer for it: that source resumes on
 * `/integrations?add_inbox=1`, so "connect Odoo" was answered with the
 * add-a-second-inbox ADD-ON BILLING modal (GET-13956). Both spellings count —
 * `?connect_app=` for a catalog app and `?connect=` for a built-in one, which
 * a signed-out Microsoft recipient of a `/go` link now arrives holding.
 *
 * WHICH integration is carried by `rememberPendingRoute`, which keeps the whole
 * URL; this only has to stop naming a destination that contradicts it, for the
 * case where the remembered URL is lost (a private window, cleared session
 * storage).
 *
 * Note the assistant's link does land here signed out, contrary to what this
 * comment said until GET-13956: `connect_integration` builds a plain
 * `/integrations?connect_app=` URL, not an `/api/v1/auth/app-link` one.
 */
function integrationsLoginSearchFromSearch(search: string): string {
  const params = new URLSearchParams(search)
  // A Settings section link is a third errand, and the add-inbox modal is
  // just as wrong an answer to it (GET-18882).
  const section = settingsSectionFromSearch(params)
  if (section !== null) {
    return `?${new URLSearchParams({ source: 'settings', [SETTINGS_SECTION_PARAM]: section })}`
  }
  const source: AppDestination = params.get('connect_app') || params.get('connect')
    ? 'integrations'
    : CONNECT_INBOX_LOGIN_SOURCE
  return `?source=${source}`
}

function normalizedRouteKey(pathname: string): string {
  return pathname.toLowerCase().replace(/\/+$/, '') || '/'
}

/** Login destination for a protected route, including any OAuth return intent. */
export function loginPathForRoute(pathname: string, search: string): string {
  const resolveSearch = LOGIN_SEARCH_BY_ROUTE[normalizedRouteKey(pathname)]
  return `${LOGIN_ROUTE}${resolveSearch?.(search) ?? ''}`
}

/**
 * Where a shared app link lands after OAuth, keyed by the `source` the backend
 * round-tripped through the OAuth `state` (and, for Microsoft — whose MSAL
 * state carries nothing — through the callback params).
 *
 * One table rather than a branch per link. It mirrors `APP_DESTINATION_PATHS`
 * in backend/src/app/utils/app_links.py: the backend decides where a SIGNED-IN
 * recipient goes and this decides where a SIGNED-OUT one lands after signing
 * in, so the two halves of the same link live in two files and must agree.
 *
 * `bypassOnboarding` is per-destination because it genuinely differs. Referral
 * entry has always skipped onboarding — someone arriving to claim a referral is
 * not there to finish setup — while everything else lets an unfinished
 * onboarding win, since a half-set-up account has no inbox card, no team and no
 * billing page worth landing on.
 */
export type AppDestination =
  | 'home'
  | 'integrations'
  | 'connect-inbox'
  | 'pricing'
  | 'checkout'
  | 'upgrade'
  | 'update-payment'
  | 'team'
  | 'refer'
  | 'refer_ambassador'
  | 'view'
  | 'location-setup'
  | 'settings'

export const APP_PATH_BY_DESTINATION: Readonly<
  Record<AppDestination, { path: string, bypassOnboarding: boolean }>
> = {
  home: { path: '/', bypassOnboarding: false },
  integrations: { path: '/integrations', bypassOnboarding: false },
  // The add-on modal, not the provider consent behind it — each inbox is billed
  // as its own add-on, so billing consent is not something a link may skip.
  // Deliberately not `?connect=`: that name is the Settings page's integration
  // deep link, whose cleanup effect strips the whole query string on mount, so
  // a flag parked there is gone before the inbox card ever reads it.
  'connect-inbox': { path: '/integrations?add_inbox=1', bypassOnboarding: false },
  pricing: { path: '/pricing', bypassOnboarding: false },
  checkout: { path: '/pricing?checkout=true', bypassOnboarding: false },
  // Converts an existing trial in place rather than starting a fresh one.
  upgrade: { path: '/pricing?upgrade=1', bypassOnboarding: false },
  'update-payment': { path: '/pricing?portal=true', bypassOnboarding: false },
  team: { path: '/team', bypassOnboarding: false },
  // The Settings card where the owner wires their phone's Arrive/Leave
  // automation to the location shortcut (GET-18103): the Shortcut section,
  // with its location steps open.
  'location-setup': { path: '/integrations?section=iphone-action-button&location=1', bypassOnboarding: false },
  // The Settings page, served at /integrations (/settings is only an alias);
  // `settingsSectionPath` adds the section a link names (GET-18882).
  settings: { path: '/integrations', bypassOnboarding: false },
  refer: { path: '/refer', bypassOnboarding: true },
  refer_ambassador: { path: '/refer?program=ambassador', bypassOnboarding: true },
  // A page the assistant built. Readable before setup is finished, because the
  // assistant can make one for someone mid-onboarding and the link must open.
  view: { path: '/views', bypassOnboarding: true },
}

/** Route of the assistant views index; one view lives at `/views/<id>`. */
export const VIEWS_ROUTE = APP_PATH_BY_DESTINATION.view.path

const VIEW_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * The page for one assistant view, or null for an id that isn't one.
 *
 * Mirrors `ViewTarget.page_path` in backend/src/app/utils/app_links.py: the
 * backend builds this path for a signed-in recipient, the OAuth callback builds
 * it here for one who had to sign in first, from the `view` the backend carried
 * through the sign-in.
 */
export function viewPath(viewId: string | null): string | null {
  if (!viewId || !VIEW_ID_PATTERN.test(viewId)) return null
  return `${VIEWS_ROUTE}/${viewId.toLowerCase()}`
}

/** Whether a pathname is one assistant view's page. */
export function isViewPath(pathname: string): boolean {
  const [, first, id, ...rest] = pathname.split('/')
  return `/${first}` === VIEWS_ROUTE && rest.length === 0 && viewPath(id ?? null) !== null
}

/**
 * Marks a /login visit as the assistant's "add a second inbox" hand-off, so the
 * callback can resume where the link was going instead of dropping the user on
 * the dashboard.
 *
 * Set by the backend's /api/v1/auth/app-link redirect, which is what a
 * signed-out user following that link hits first. Must match
 * `AppDestination.ADD_INBOX` in backend/src/app/utils/app_links.py.
 */
export const CONNECT_INBOX_LOGIN_SOURCE = 'connect-inbox'

/** Where that hand-off lands. */
export const CONNECT_INBOX_RETURN_PATH =
  APP_PATH_BY_DESTINATION[CONNECT_INBOX_LOGIN_SOURCE].path

/** Query flag that opens the add-on modal on the inbox card. */
export const CONNECT_INBOX_PARAM = 'add_inbox'
export const CONNECT_INBOX_PARAM_VALUE = '1'

/**
 * The integrations page deep-linked to one integration's connect flow.
 *
 * Mirrors `ConnectTarget.page_params` in backend/src/app/utils/app_links.py,
 * for the same reason `APP_PATH_BY_DESTINATION` mirrors its table: the backend
 * builds this path for a signed-IN recipient, and the OAuth callback builds it
 * here for one who had to sign in first, so the two halves of one link live in
 * two files and must agree.
 *
 * Two shapes because the page has two. A built-in integration is named by slug
 * alone; a Pipedream catalog app also carries its display name, because that
 * card opens its catalog by free-text search and a slug like `google_sheets` is
 * the name of nothing in it.
 */
export function integrationConnectPath(
  slug: string,
  catalogName: string | null,
): string {
  const params = catalogName
    ? { connect_app: slug, connect_app_name: catalogName }
    : { connect: slug }
  return `${APP_PATH_BY_DESTINATION.integrations.path}?${new URLSearchParams(params)}`
}

/**
 * The Settings page scrolled to one section, or null for an id this build does
 * not render.
 *
 * Mirrors `SettingsTarget.page_path` in backend/src/app/utils/app_links.py,
 * for the same reason `viewPath` mirrors `ViewTarget.page_path`.
 */
export function settingsSectionPath(section: string | null): string | null {
  if (!isSettingsSection(section)) return null
  const query = new URLSearchParams({ [SETTINGS_SECTION_PARAM]: section })
  return `${APP_PATH_BY_DESTINATION.settings.path}?${query}`
}

/**
 * Where the OAuth callback should land for an entry `source`, or null when the
 * source names no destination (a cold sign-in, or a slug this build predates).
 */
export function returnPathForLoginSource(
  source: string | null,
): { path: string, bypassOnboarding: boolean } | null {
  // A `source` off the wire is any string: an old link, a hand-typed one, or a
  // slug added after this build shipped. An unknown one is not an error — it
  // just means "no destination", and the caller falls through to its normal
  // post-sign-in routing.
  if (!source || !isAppDestination(source)) return null
  return APP_PATH_BY_DESTINATION[source]
}

function isAppDestination(source: string): source is AppDestination {
  return Object.hasOwn(APP_PATH_BY_DESTINATION, source)
}

/**
 * Check if a given path is a public route that doesn't require authentication
 * @param path - The pathname to check (e.g., window.location.pathname)
 * @returns true if the path is a public route
 */
export function isPublicRoute(path: string): boolean {
  const normalizedPath = path.toLowerCase()
  return (
    normalizedPath === '/' ||
    normalizedPath.startsWith('/login') ||
    normalizedPath.startsWith(CALLBACK_ROUTE) ||
    normalizedPath.startsWith(PENDING_ROUTE) ||
    normalizedPath.startsWith('/privacy') ||
    normalizedPath.startsWith('/terms') ||
    normalizedPath.startsWith('/support') ||
    normalizedPath.startsWith('/slack') ||
    normalizedPath.startsWith('/zoom') ||
    normalizedPath.startsWith('/pricing') ||
    normalizedPath.startsWith('/ambassadors') ||
    normalizedPath.startsWith('/billing/portal') ||
    normalizedPath.startsWith('/travel-card/saved') ||
    normalizedPath.startsWith('/s/') ||
    normalizedPath.startsWith('/stays/') ||
    normalizedPath.startsWith('/flights/') ||
    normalizedPath.startsWith('/trips/') ||
    // CC-to-schedule concierge availability page: the organizer has no Catch
    // account, so this must skip the auth check / 401-redirect (GET-8904).
    normalizedPath.startsWith('/cc/') ||
    normalizedPath.startsWith('/connect-email') ||
    // Magic-link secret entry: the signed token in the query string is the
    // auth. The owner opens these from a chat on a phone with no Catch session,
    // so the mount-time /auth/me 401 must not bounce them to /login (GET-16192).
    normalizedPath.startsWith('/vault/enter-secret') ||
    normalizedPath.startsWith('/integrations/enter-secret')
  )
}
